import {gsap} from "gsap";
import NavHeight from "../utils/nav-height";

export default class Nav {
    constructor(options = {}) {
        this.enableNavAutohide = !!options.enableNavAutohide;
        this.navHeightInstance = new NavHeight();
        this.activeItem = null;
        this.nav = document.querySelector('.nav-menu');
        this.darkSections = document.querySelectorAll('[data-dark-bg]');
        this._lastScrollY = 0;
        this._navIsHidden = false;

        this.init();
    }

    init() {
        this.preventEmptyLinks();
        this.initHoverSubMenus();
        this.updateMegaHeights();
        this.initMegaPanels();
        this.handleOnDark();
        window.addEventListener('resize', () => {
            this.handleOnDark();
            this.updateMegaHeights();
        });
        window.addEventListener('mousemove', (e) => {
            if (e.clientY <= 30) {
                this.showNav();
                if (window.scrollY > 0) {
                    this.nav.classList.add('has-background');
                } else {
                    this.nav.classList.remove('has-background');
                }
            }
        });
    }


    onScroll({ scroll }) {
        if (this.enableNavAutohide) {
            this.handleNavAutoHide(scroll);
        }
        this.handleOnDark();
    }

    handleNavAutoHide(scrollY) {
        if (!this.enableNavAutohide) return;

        const threshold = 5;
        const navResponsive = document.querySelector('.nav-responsive');

        if (scrollY < 20) {
            this.showNav();
            this.nav.classList.remove('has-background');
            navResponsive?.classList.remove('has-background');
        } else if (scrollY < this._lastScrollY - threshold) {
            this.showNav();
            this.nav.classList.add('has-background');
            navResponsive?.classList.add('has-background');
        } else if (scrollY > this._lastScrollY + threshold) {
            this.hideNav();
            navResponsive?.classList.remove('has-background');
        }

        this._lastScrollY = scrollY;
    }

    showNav() {
        if (this._navIsHidden) {
            this.nav.style.transform = "translateY(0)";
            this._navIsHidden = false;
        }
    }

    hideNav() {
        if (!this._navIsHidden) {
            this.nav.style.transform = `translateY(-${this.nav.offsetHeight}px)`;
            this.nav.style.transition = "transform 0.35s cubic-bezier(.83,.06,.34,1)";
            this._navIsHidden = true;
            this.nav.classList.remove('has-background');
        }
    }

    handleOnDark() {
        if (!this.nav) return;

        const navResponsive = document.querySelector('.nav-responsive');
        // const overlay = document.querySelector('.nav-overlay');

        const navRect = this.nav.getBoundingClientRect();
        const probeX  = Math.round(window.innerWidth / 2);
        const probeY  = Math.min(window.innerHeight - 1, Math.max(0, Math.round(navRect.bottom + 1)));

        const uiEls = [this.nav, navResponsive].filter(Boolean);
        const savedPE = uiEls.map(el => el.style.pointerEvents);
        uiEls.forEach(el => (el.style.pointerEvents = 'none'));

        const stack = document.elementsFromPoint(probeX, probeY);

        uiEls.forEach((el, i) => (el.style.pointerEvents = savedPE[i]));

        const isUi = el => uiEls.some(ui => ui === el || ui.contains(el));
        const firstContentEl = stack.find(el => !isUi(el));

        const carrier = firstContentEl?.closest?.('[data-dark-bg]');
        const isOnDark = !!carrier;

        this.nav.classList.toggle('on-dark', isOnDark);
        navResponsive?.classList.toggle('on-dark', isOnDark);
    }



    // === HOVER SUB-MENU ===
    initHoverSubMenus() {
        const menuItems = document.querySelectorAll(".menu_item-has-children");
        // const overlay = document.querySelector('.nav-overlay');
        const timeouts = new Map();

        const measureSubMenuHeight = (menu) => {
            const originalStyles = {
                visibility: menu.style.visibility,
                position: menu.style.position,
                display: menu.style.display
            };

            menu.style.visibility = "hidden";
            menu.style.position = "absolute";
            menu.style.display = "block";

            const menuHeight = menu.scrollHeight;

            menu.style.visibility = originalStyles.visibility;
            menu.style.position = originalStyles.position;
            menu.style.display = originalStyles.display;

            let maxChildHeight = menuHeight;
            menu.querySelectorAll(".sub-menu").forEach(subMenu => {
                const subMenuHeight = measureSubMenuHeight(subMenu);
                if (subMenuHeight > maxChildHeight) {
                    maxChildHeight = subMenuHeight;
                }
            });

            return maxChildHeight;
        };

        document.querySelectorAll(".nav-menu .megamenu").forEach(megamenu => {
            let maxSubMenuHeight = 0;

            megamenu.querySelectorAll(".sub-menu").forEach(subMenu => {
                const subMenuHeight = measureSubMenuHeight(subMenu);
                if (subMenuHeight > maxSubMenuHeight) {
                    maxSubMenuHeight = subMenuHeight;
                }
            });

            megamenu.style.setProperty("--max-submenu-height", `${maxSubMenuHeight}px`);
        });

        const clearAllTimeouts = () => {
            timeouts.forEach((timeout, key) => {
                clearTimeout(timeout);
                timeouts.delete(key);
            });
        };

        menuItems.forEach((item) => {
            const megamenu = item.querySelector(":scope > .megamenu");
            const subMenus = item.querySelectorAll(":scope > .sub-menu");
            const menuId = `menu-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const handleMouseEnter = () => {
                clearAllTimeouts();

                if (this.activeItem && this.activeItem !== item) {
                    this.activeItem.classList.remove("hover-active");
                    const previousMegamenu = this.activeItem.querySelector(":scope > .megamenu");

                    if (previousMegamenu) {
                        gsap.killTweensOf(previousMegamenu);
                        gsap.set(previousMegamenu, { opacity: 0, visibility: 'hidden', y: -this.navHeightInstance.navHeightValue / 2 });
                    }
                }

                this.activeItem = item;

                if (!item.classList.contains("hover-active")) {
                    item.classList.add("hover-active");
                    // overlay.classList.add("active");

                    const offsetY = this.navHeightInstance.navHeightValue / 2;

                    if (megamenu) {
                        if (typeof megamenu._restoreBase === 'function') {
                            megamenu._restoreBase();
                        }

                        gsap.killTweensOf(megamenu);
                        gsap.fromTo(megamenu,
                            { opacity: 0, visibility: 'hidden', y: -offsetY },
                            { opacity: 1, visibility: 'visible', y: 0, duration: 0.35, ease: "power2.out", overwrite: "auto" }
                        );
                    }
                }
            };

            const handleMouseLeave = () => {
                const timeout = setTimeout(() => {
                    const relatedTarget = document.activeElement;
                    const isHoveringAnotherMenu = Array.from(menuItems).some(
                        otherItem => otherItem !== item && otherItem.contains(relatedTarget)
                    );

                    if (!isHoveringAnotherMenu) {
                        item.classList.remove("hover-active");
                        this.activeItem = null;

                        if (megamenu) {
                            const offsetY = this.navHeightInstance.navHeightValue / 2;

                            if (typeof megamenu._restoreBase === 'function') {
                                megamenu._restoreBase(); // remet l’état base OU rien selon le mode
                            }

                            gsap.killTweensOf(megamenu);
                            gsap.to(megamenu, {
                                opacity: 0, visibility: 'hidden', y: -offsetY, duration: 0.35, overwrite: "auto"
                            });
                        }

                    }

                    timeouts.delete(menuId);
                }, 200);

                timeouts.set(menuId, timeout);
            };


            item.addEventListener("mouseenter", handleMouseEnter);
            item.addEventListener("mouseleave", handleMouseLeave);

            if (megamenu) {
                megamenu.addEventListener("mouseenter", handleMouseEnter);
                megamenu.addEventListener("mouseleave", handleMouseLeave);
            }

            subMenus.forEach((subMenu) => {
                if (subMenu) {
                    subMenu.addEventListener("mouseenter", handleMouseEnter);
                    subMenu.addEventListener("mouseleave", handleMouseLeave);
                }
            });
        });
        this.initMegaPanels();
    }

    initMegaPanels() {
        document.querySelectorAll(".megamenu").forEach(mega => {
            if (mega.dataset.mmBound === "1") return;
            mega.dataset.mmBound = "1";

            // ====== Raccourcis / cache locaux ======
            const tabs   = mega.querySelectorAll(".megamenu__tab[data-panel-id]");
            const panels = mega.querySelectorAll(".megamenu__panel");
            const rightCol = mega.querySelector(".megamenu_panels-right");
            const panelsContainer = mega.querySelector(".megamenu_panels");
            if (!tabs.length || !panels.length) return;

            // ====== Helpers “purs” ======
            const qsa = (sel, root = mega) => root.querySelectorAll(sel);
            const qs  = (sel, root = mega) => root.querySelector(sel);

            const panelHasContent = (panel) => {
                if (!panel) return false;
                if (panel.dataset?.empty === "1") return false;
                return !!panel.querySelector(".megamenu__link");
            };

            const setRight = (has) => {
                if (!rightCol) return;
                rightCol.toggleAttribute("hidden", !has);
                rightCol.classList.toggle("is-active", !!has);
                panelsContainer?.setAttribute("data-right", has ? "1" : "0");
            };

            const clearDimPanel = (panel) => {
                panel?.querySelectorAll(".megamenu__link.is-dimmed")
                    .forEach(a => a.classList.remove("is-dimmed"));
            };

            const getActiveLinkInPanel = (panel) => {
                return (
                    panel.querySelector(".megamenu__link.is-active") ||
                    panel.querySelector('.megamenu__link[aria-current="page"]') ||
                    panel.querySelector("li.current > .megamenu__link") ||
                    null
                );
            };

            const applyBaseDimming = (panel) => {
                clearDimPanel(panel);
                const active = getActiveLinkInPanel(panel);
                if (!active) return;
                panel.querySelectorAll(".megamenu__link").forEach(a => {
                    if (a !== active) a.classList.add("is-dimmed");
                });
            };

            const clearDimAll = () => {
                qsa(".is-dimmed,.is-hover,.is-hovering,.is-active-left")
                    .forEach(el => el.classList.remove("is-dimmed","is-hover","is-hovering","is-active-left"));
                qsa(".megamenu__tab.is-active").forEach(el => el.classList.remove("is-active"));
            };

            const deactivateAllPanels = () => {
                panels.forEach(p => { p.classList.remove("is-active"); p.setAttribute("hidden", ""); });
                tabs.forEach(t => t.classList.remove("is-active","is-hover"));
                qsa(".megamenu_panels-left li").forEach(li => li.classList.remove("is-active-left","is-hover"));
                qsa(".megamenu__link.is-dimmed").forEach(a => a.classList.remove("is-dimmed"));
                setRight(false);
            };

            const setActiveLeft = (id, persist) => {
                qsa(".megamenu_panels-left li").forEach(li => li.classList.remove("is-active-left","is-hover"));
                const leftTab = qs(`.megamenu__tab[data-panel-id="${id}"]`);
                const leftLi  = leftTab?.closest("li");
                if (!leftLi) return;
                leftLi.classList.add(persist ? "is-active-left" : "is-hover");
            };

            const show = (id, { persist = false } = {}) => {
                clearDimAll();

                let currentPanel = null;
                panels.forEach(p => {
                    const active = (p.id === id);
                    p.toggleAttribute("hidden", !active);
                    p.classList.toggle("is-active", active);
                    if (active) currentPanel = p; else clearDimPanel(p);
                });

                if (mode === "base") {
                    tabs.forEach(t => { t.classList.toggle("is-active", t.dataset.panelId === id); t.classList.remove("is-hover"); });
                    setActiveLeft(id, persist);
                } else {
                    tabs.forEach(t => { t.classList.toggle("is-hover", t.dataset.panelId === id); t.classList.remove("is-active"); });
                    setActiveLeft(id, false);
                }

                const hasRight = !!currentPanel && panelHasContent(currentPanel);
                setRight(hasRight);

                if (mega.dataset.hoverOnly !== "1" && currentPanel) {
                    applyBaseDimming(currentPanel);
                }

                this.updateMegaHeights();
            };

            const restoreBase = () => {
                if (mode === "base" && mega.dataset.basePanelId) {
                    const basePanel = qs(`#${mega.dataset.basePanelId}`);
                    setRight(panelHasContent(basePanel));
                    show(mega.dataset.basePanelId, { persist: true });
                } else {
                    deactivateAllPanels();
                }
            };
            mega._restoreBase = restoreBase;

            // ====== Détection “mode” & basePanelId ======
            const currentNoPanel     = !!qs(".megamenu_panels-left li.current:not(.sub-menu_item-has-children)");
            const currentWithPanel   =  qs(".megamenu_panels-left li.sub-menu_item-has-children.current .megamenu__tab[data-panel-id]");
            const ancestorWithPanel  =  qs(".megamenu_panels-left li.sub-menu_item-has-children.current-ancestor .megamenu__tab[data-panel-id]");

            const activeRightLink    =
                qs(".megamenu__panel .megamenu__link.is-active") ||
                qs('.megamenu__panel .megamenu__link[aria-current="page"]') ||
                qs(".megamenu__panel li.current > .megamenu__link");

            const panelOfActiveRight = activeRightLink?.closest(".megamenu__panel");

            let basePanelId =
                panelOfActiveRight?.id ||
                currentWithPanel?.dataset.panelId ||
                ancestorWithPanel?.dataset.panelId ||
                null;

            const mode = basePanelId ? "base" : (currentNoPanel ? "hoverOnly" : "none");

            if (mode === "hoverOnly") mega.dataset.hoverOnly = "1"; else delete mega.dataset.hoverOnly;
            if (basePanelId) mega.dataset.basePanelId = basePanelId; else delete mega.dataset.basePanelId;

            // ====== État initial ======
            if (mode === "base") {
                const basePanel = qs(`#${mega.dataset.basePanelId}`);
                setRight(panelHasContent(basePanel));  // fixe data-right avant show
                show(mega.dataset.basePanelId, { persist: true });
            } else {
                deactivateAllPanels(); // met aussi data-right à 0
                delete mega.dataset.basePanelId;
            }

            // ====== Listeners ======
            tabs.forEach(tab => {
                const id = tab.dataset.panelId;
                tab.addEventListener("mouseenter", () => show(id, { persist:false }));
                tab.addEventListener("focus",      () => show(id, { persist:false }));
            });

            panels.forEach(panel => {
                const leftTab = qs(`.megamenu__tab[data-panel-id="${panel.id}"]`);
                const leftLi  = leftTab?.closest("li");

                panel.addEventListener("mouseenter", () => {
                    leftTab?.classList.add("is-hover");
                    leftLi?.classList.add("is-hover");
                });

                panel.addEventListener("mouseleave", () => {
                    leftTab?.classList.remove("is-hover");
                    leftLi?.classList.remove("is-hover");
                    panel.classList.remove("is-hovering");
                    clearDimPanel(panel);
                    if (mega.dataset.hoverOnly !== "1") {
                        applyBaseDimming(panel);
                    }
                });

                panel.querySelectorAll(".megamenu__link").forEach(link => {
                    const activate = () => {
                        panel.classList.add("is-hovering");
                        clearDimPanel(panel);
                        panel.querySelectorAll(".megamenu__link").forEach(a => { if (a !== link) a.classList.add("is-dimmed"); });
                        leftTab?.classList.add("is-hover");
                        leftLi?.classList.add("is-hover");
                    };
                    const deactivate = () => {
                        panel.classList.remove("is-hovering");
                        clearDimPanel(panel);
                        if (mega.dataset.hoverOnly !== "1") {
                            applyBaseDimming(panel);
                        }
                    };
                    link.addEventListener("mouseenter", activate);
                    link.addEventListener("mouseleave", deactivate);
                    link.addEventListener("focus",      activate);
                    link.addEventListener("blur",       deactivate);
                });
            });

            const plainLeftTabs = qsa(".megamenu_panels-left .megamenu__tab:not([data-panel-id])");
            plainLeftTabs.forEach(a => {
                const li = a.closest("li");

                const enterPlain = () => {
                    qsa(".megamenu__tab.is-active").forEach(t => t.classList.remove("is-active"));
                    qsa(".megamenu_panels-left li").forEach(x => x.classList.remove("is-active-left","is-hover"));
                    qsa(".megamenu__link.is-dimmed").forEach(x => x.classList.remove("is-dimmed"));
                    panels.forEach(p => { p.classList.remove("is-active"); p.setAttribute("hidden",""); });
                    setRight(false);
                    li?.classList.add("is-hover");
                };

                const leavePlain = () => {
                    li?.classList.remove("is-hover");
                    if (typeof mega._restoreBase === "function") mega._restoreBase();
                };

                a.addEventListener("mouseenter", enterPlain);
                a.addEventListener("focus",      enterPlain);
                a.addEventListener("mouseleave", leavePlain);
                a.addEventListener("blur",       leavePlain);
            });

            // reset auto en sortie : base -> restore ; none/hoverOnly -> tout nettoyer
            mega.addEventListener("mouseleave", mega._restoreBase);
        });
    }




    updateMegaHeights() {
        document.querySelectorAll(".nav-menu .megamenu").forEach(megamenu => {
            let maxPanelHeight = 0;

            megamenu.querySelectorAll(".megamenu__panel").forEach(p => {
                const wasHidden = p.hasAttribute("hidden");
                if (wasHidden) p.removeAttribute("hidden");
                maxPanelHeight = Math.max(maxPanelHeight, p.scrollHeight);
                if (wasHidden) p.setAttribute("hidden", "");
            });

            const current = parseFloat(getComputedStyle(megamenu).getPropertyValue("--max-submenu-height")) || 0;
            megamenu.style.setProperty("--max-submenu-height", `${Math.max(current, maxPanelHeight)}px`);
        });
    }

    preventEmptyLinks() {
        if (!this.nav) return;

        this.nav.addEventListener('click', (e) => {
            const link = e.target.closest('a');
            if (link && link.getAttribute('href') === '#') {
                e.preventDefault();
            }
        });
    }

}
